import { useCallback, useEffect, useState, useMemo } from 'react';

import { fetchJson } from '@api/ApiHelper';

import {
  PeoplePageResponse,
  PeoplePage,
} from '@components/people_tags/customPeopleTagTypes';

import { usePeopleManagementContext } from '../PeopleManagementContext';

// API response type from the faces endpoint
interface FaceApiResponse {
  id: string;
  thumbnail_url: string;
  source_url: string;
  identity_name: string;
  bounding_box: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  name: string;
}

const transformFaceDataToResponse = (faces: FaceApiResponse[]): PeoplePageResponse => {
  const transformedData: PeoplePage[] = faces.map((face) => ({
    thumbnail_url: face.thumbnail_url || '',
    person_id: face.id,
    bounding_box: face.bounding_box,
    name: face.name || '',
  }));

  return {
    data: transformedData,
    meta: {
      bf_processed: true,
      current_page: 1,
      next_page: null,
      prev_page: null,
      total_count: transformedData.length,
      total_pages: 1,
    },
  };
};

export interface UseFetchPeopleHubDataReturn {
  initialLoadingState: boolean;
  peopleData: PeoplePage[];
  allPeopleData: PeoplePage[];
  peopleDataError: boolean;
  peopleDataLoading: boolean;
  refetchPeopleData: () => Promise<void>;
  allCount: number;
  namedCount: number;
  unnamedCount: number;
  bfProcessed: boolean;
  totalPages: number;
  currentPage: number;
}

const ITEMS_PER_PAGE = 24;

export const useFetchPeopleHubData = (): UseFetchPeopleHubDataReturn => {
  const { state } = usePeopleManagementContext();
  const [rawPeopleData, setRawPeopleData] = useState<PeoplePageResponse>(undefined);
  const [peopleDataLoading, setPeopleDataLoading] = useState<boolean>(true);
  const [initialLoadingState, setInitialLoadingState] = useState<boolean>(true);
  const [peopleDataError, setPeopleDataError] = useState<boolean>(false);

  const loadPeopleData = useCallback(async (): Promise<void> => {
    try {
      setPeopleDataLoading(true);
      setRawPeopleData(undefined);

      // Get brandfolder key from global BFG object
      const brandfolderKey = BFG.brandfolder_key;

      if (!brandfolderKey) {
        throw new Error('Brandfolder key not found');
      }

      // Make API call to fetch identities
      const response = await fetchJson<FaceApiResponse[]>({
        url: `/api/v4/private/people_tagging/brandfolders/${brandfolderKey}/identities`,
        method: 'GET',
      });

      // Transform API response to expected format
      const transformedData = transformFaceDataToResponse(response);
      setRawPeopleData(transformedData);
      setPeopleDataError(false);
    } catch (error) {
      console.error('Error fetching people data:', error);
      setRawPeopleData(undefined);
      setPeopleDataError(true);
    } finally {
      setPeopleDataLoading(false);
      setInitialLoadingState(false);
    }
  }, []);

  useEffect(() => {
    loadPeopleData();
  }, [loadPeopleData]);

  // Calculate counts and apply filtering/sorting/pagination based on state
  const { filteredData, allCount, namedCount, unnamedCount, totalPages } = useMemo(() => {
    if (!rawPeopleData?.data) {
      return { filteredData: [], allCount: 0, namedCount: 0, unnamedCount: 0, totalPages: 0 };
    }

    const allData = rawPeopleData.data;
    const named = allData.filter((person) => person.name && person.name.trim() !== '');
    const unnamed = allData.filter((person) => !person.name || person.name.trim() === '');

    let filtered = [...allData];

    // Apply search filter
    if (state.search.trim()) {
      const searchTerm = state.search.toLowerCase();
      filtered = filtered.filter((person) => {
        const name = person.name || 'unnamed';
        return name.toLowerCase().includes(searchTerm);
      });
    }

    // Apply category filter
    switch (state.filter) {
      case 'named':
        filtered = filtered.filter((person) => person.name && person.name.trim() !== '');
        break;
      case 'unnamed':
        filtered = filtered.filter((person) => !person.name || person.name.trim() === '');
        break;
      case 'all':
      default:
        // No additional filtering needed
        break;
    }

    // Apply sorting
    switch (state.sort) {
      case 'name_asc':
        filtered.sort((a, b) => {
          const nameA = a.name || 'unnamed';
          const nameB = b.name || 'unnamed';
          return nameA.localeCompare(nameB);
        });
        break;
      case 'name_desc':
        filtered.sort((a, b) => {
          const nameA = a.name || 'unnamed';
          const nameB = b.name || 'unnamed';
          return nameB.localeCompare(nameA);
        });
        break;
      case 'recent':
      default:
        // Keep original order for "most recent"
        break;
    }

    // Calculate total pages
    const calculatedTotalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

    // Apply pagination
    const startIndex = (state.page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedData = filtered.slice(startIndex, endIndex);

    return {
      filteredData: paginatedData,
      allCount: allData.length,
      namedCount: named.length,
      unnamedCount: unnamed.length,
      totalPages: calculatedTotalPages
    };
  }, [rawPeopleData?.data, state.search, state.filter, state.sort, state.page]);

  return {
    initialLoadingState,
    peopleData: filteredData,
    allPeopleData: rawPeopleData?.data || [],
    peopleDataError,
    peopleDataLoading,
    refetchPeopleData: loadPeopleData,
    allCount,
    namedCount,
    unnamedCount,
    bfProcessed: rawPeopleData?.meta.bf_processed ?? false,
    totalPages,
    currentPage: state.page,
  };
};
